import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Heading,
  Text,
  Button,
  Loader,
  Toast,
  Flex,
  Box,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@vibe/core'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { EvaluatePostulacionGate } from '../components/common/PermissionGate'
import { CVUpload } from '../components/forms/CVUpload'
import { CandidatesTable, JobInformation } from '../components/dashboard'
import JobIntegrationSection from '../components/forms/JobIntegrationSection'
import { apiService } from '../services/api'
import { usePermissions } from '../hooks/usePermissions'
import type {
  CandidatesResponse,
  Candidate,
  StatusUpdate,
  SolicitudDetailResponse,
  UploadResult,
} from '../types'
import { getErrorMessage } from '../utils/errorHandling'
import { UserRole, QualificationStatus } from '../types'

/**
 * Candidates Management page with integrated upload functionality and table management
 */
const CandidatesManagement: React.FC = () => {
  const { solicitudId } = useParams<{ solicitudId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // State management
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [solicitudData, setSolicitudData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [filterLoading, setFilterLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(
    new Set()
  )
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false)
  const [processingPolling, setProcessingPolling] = useState(false)

  // Pagination and filtering state
  const [pageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [nextKey, setNextKey] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [qualificationFilter, setQualificationFilter] = useState<string>('all')
  const [sortField, setSortField] = useState('fecha_registro')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Get user permissions for role-based filtering
  const { hasRole } = usePermissions()
  const permissions = usePermissions()

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const [toastType, setToastType] = useState<'positive' | 'negative'>(
    'positive'
  )

  // Tab state - check URL parameter for initial tab
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab')
    return tabParam === 'requirement' ? 1 : 0
  }
  const [activeTab, setActiveTab] = useState(getInitialTab())

  // Create tabs array based on permissions
  const tabs = [
    { value: 0, label: 'Proceso de Postulación' },
    { value: 1, label: 'Información de la Vacante' },
  ]

  if (permissions?.canManageUsers()) {
    tabs.push({ value: 2, label: 'Integraciones' })
  }

  /**
   * Load candidates data from API
   */
  const loadCandidates = useCallback(
    async (resetPagination = false, customNextKey?: string) => {
      if (!solicitudId) return

      try {
        setLoading(true)
        setError(null)

        // Apply role-based filtering for Viewers
        const effectiveQualificationFilter =
          hasRole(UserRole.VISUALIZADOR) &&
          !hasRole(UserRole.EDITOR) &&
          !hasRole(UserRole.ADMINISTRADOR)
            ? 'califica' // Viewers can only see qualified candidates
            : qualificationFilter

        const params = {
          id_solicitud: solicitudId,
          size: pageSize,
          ...(resetPagination ? {} : { start: customNextKey }),
          ...(statusFilter !== 'all' && { estado: statusFilter }),
          ...(effectiveQualificationFilter !== 'all' && {
            qualification_status: effectiveQualificationFilter,
          }),
          sort_field: sortField,
          sort_order: sortOrder,
        }

        const response: CandidatesResponse =
          await apiService.getCandidates(params)

        // Extract data from the response structure
        const { data } = response
        const items = data.items || []
        const totalItems = data.total_items || 0
        const responseNextKey = data.next_key
        const solicitud = data.solicitud

        if (resetPagination) {
          setCandidates(items)
        } else {
          setCandidates(prev => [...(prev || []), ...items])
        }

        setTotalItems(totalItems)
        setNextKey(responseNextKey)
        setSolicitudData(solicitud)

        // Show upload area if no candidates exist
        if (items.length === 0 && resetPagination) {
          setShowUpload(true)
        }
      } catch (err) {
        const errorMessage =
          getErrorMessage(err) || 'Error al cargar candidatos'
        const safeErrorMessage =
          typeof errorMessage === 'string'
            ? errorMessage
            : 'Error al cargar candidatos'
        setError(safeErrorMessage)
        showToast(safeErrorMessage, 'negative')
      } finally {
        setLoading(false)
        setFilterLoading(false)
      }
    },
    [
      solicitudId,
      pageSize,
      statusFilter,
      qualificationFilter,
      sortField,
      sortOrder,
      hasRole,
    ]
  )

  /**
   * Load solicitud details
   */
  const loadSolicitudDetails = useCallback(async () => {
    if (!solicitudId) return

    try {
      const response: SolicitudDetailResponse =
        await apiService.getSolicitud(solicitudId)
      setSolicitudData(response.data)
    } catch (err) {
      console.warn('Could not load solicitud details:', err)
    }
  }, [solicitudId])

  /**
   * Handle successful CV upload
   */
  const handleUploadSuccess = useCallback(
    (result: UploadResult) => {
      console.log('Upload completed:', result)

      // Check if postulaciones were created successfully
      if (result.completed && result.completed.postulaciones.length > 0) {
        const count = result.completed.postulaciones.length
        setShowUpload(false)
        loadCandidates(true)
        showToast(
          `${count} CV${count > 1 ? 's' : ''} subido${count > 1 ? 's' : ''} exitosamente. Los candidatos están siendo procesados.`,
          'positive'
        )
      } else {
        // Upload succeeded but no postulaciones were created
        console.error(
          'Upload completed but no postulaciones were created:',
          result
        )
        showToast(
          'Los archivos se subieron pero no se pudieron crear los registros de candidatos. Por favor, contacte al administrador.',
          'negative'
        )
      }
    },
    [loadCandidates]
  )

  /**
   * Handle bulk status update
   */
  const handleBulkStatusUpdate = async (
    newStatus: 'califica' | 'no_califica' | 'pendiente'
  ) => {
    if (selectedCandidates.size === 0) {
      showToast('Selecciona al menos un candidato', 'negative')
      return
    }

    try {
      setBulkUpdateLoading(true)

      const updates: StatusUpdate[] = Array.from(selectedCandidates).map(
        id => ({
          id_postulacion: id,
          estado: newStatus,
        })
      )

      await apiService.updateCandidateStatus({ updates })

      // Update local state
      setCandidates(prev =>
        (prev || []).map(candidate =>
          selectedCandidates.has(candidate.id_postulacion)
            ? { ...candidate, estado: newStatus }
            : candidate
        )
      )

      setSelectedCandidates(new Set())
      showToast(
        `Estado actualizado para ${updates.length} candidato(s)`,
        'positive'
      )
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Error al actualizar estado'
      showToast(errorMessage, 'negative')
    } finally {
      setBulkUpdateLoading(false)
    }
  }

  /**
   * Handle bulk qualification update
   */
  const handleBulkQualificationUpdate = async (
    qualificationStatus: 'califica' | 'no_califica'
  ) => {
    if (selectedCandidates.size === 0) {
      showToast('Selecciona al menos un candidato', 'negative')
      return
    }

    try {
      setBulkUpdateLoading(true)

      const candidateIds = Array.from(selectedCandidates)

      await apiService.bulkUpdateCandidateQualification({
        candidate_ids: candidateIds,
        qualification_status: qualificationStatus,
      })

      // Update local state
      setCandidates(prev =>
        (prev || []).map(candidate =>
          selectedCandidates.has(candidate.id_postulacion)
            ? {
                ...candidate,
                qualification_status: qualificationStatus,
                qualified_at: new Date().toISOString(),
              }
            : candidate
        )
      )

      setSelectedCandidates(new Set())
      const statusText =
        qualificationStatus === 'califica'
          ? 'calificados'
          : 'marcados como no califican'
      showToast(
        `${candidateIds.length} candidato(s) ${statusText} exitosamente`,
        'positive'
      )
    } catch (err) {
      const errorMessage =
        getErrorMessage(err) || 'Error al actualizar calificación'
      showToast(errorMessage, 'negative')
    } finally {
      setBulkUpdateLoading(false)
    }
  }

  /**
   * Handle candidate selection
   */
  const handleCandidateSelection = (candidateId: string, selected: boolean) => {
    setSelectedCandidates(prev => {
      const newSet = new Set(prev)
      if (selected) {
        newSet.add(candidateId)
      } else {
        newSet.delete(candidateId)
      }
      return newSet
    })
  }

  /**
   * Handle select all candidates
   */
  const handleSelectAll = (selected: boolean) => {
    if (selected && candidates) {
      setSelectedCandidates(new Set(candidates.map(c => c.id_postulacion)))
    } else {
      setSelectedCandidates(new Set())
    }
  }

  /**
   * Handle filter changes
   */
  const handleFilterChange = async (
    filterType: 'status' | 'qualification',
    value: string
  ) => {
    if (filterType === 'status') {
      setStatusFilter(value)
    } else if (filterType === 'qualification') {
      // Prevent Viewers from changing qualification filter
      if (
        hasRole(UserRole.VISUALIZADOR) &&
        !hasRole(UserRole.EDITOR) &&
        !hasRole(UserRole.ADMINISTRADOR)
      ) {
        return // Silently ignore filter changes for Viewers
      }
      setQualificationFilter(value)
    }

    setSelectedCandidates(new Set())
    // Clear pagination state when filters change
    setNextKey(undefined)
    // Show filter loading state
    setFilterLoading(true)
    loadCandidates(true)
  }

  /**
   * Handle sorting changes
   */
  const handleSortChange = async (field: string, order: 'asc' | 'desc') => {
    setSortField(field)
    setSortOrder(order)
    // Clear pagination state when sorting changes
    setNextKey(undefined)
    // Show filter loading state
    setFilterLoading(true)
    loadCandidates(true)
  }

  /**
   * Load more candidates (pagination)
   */
  const handleLoadMore = useCallback(async () => {
    if (!nextKey || loading || loadingMore || !solicitudId) {
      return
    }

    try {
      setLoadingMore(true)
      setError(null)

      // Apply role-based filtering for Viewers
      const effectiveQualificationFilter =
        hasRole(UserRole.VISUALIZADOR) &&
        !hasRole(UserRole.EDITOR) &&
        !hasRole(UserRole.ADMINISTRADOR)
          ? 'califica' // Viewers can only see qualified candidates
          : qualificationFilter

      const params = {
        id_solicitud: solicitudId,
        size: pageSize,
        start: nextKey,
        ...(statusFilter !== 'all' && { estado: statusFilter }),
        ...(effectiveQualificationFilter !== 'all' && {
          qualification_status: effectiveQualificationFilter,
        }),
        sort_field: sortField,
        sort_order: sortOrder,
      }

      const response: CandidatesResponse =
        await apiService.getCandidates(params)

      // Extract data from the response structure
      const { data } = response
      const items = data.items || []
      const responseNextKey = data.next_key

      // Append new candidates to existing ones
      setCandidates(prev => [...(prev || []), ...items])
      setNextKey(responseNextKey)
      setTotalItems(data.total_items || 0)
    } catch (err) {
      const errorMessage =
        getErrorMessage(err) || 'Error al cargar más candidatos'
      const safeErrorMessage =
        typeof errorMessage === 'string'
          ? errorMessage
          : 'Error al cargar más candidatos'

      // Check if this is a pagination key mismatch error
      if (
        safeErrorMessage.includes('starting key does not match') ||
        safeErrorMessage.includes('range key predicate')
      ) {
        // Reset pagination and reload from the beginning
        console.warn('Pagination key mismatch detected, resetting pagination')
        setNextKey(undefined)
        loadCandidates(true)
        return
      }

      setError(safeErrorMessage)
      showToast(safeErrorMessage, 'negative')
    } finally {
      setLoadingMore(false)
    }
  }, [
    nextKey,
    loading,
    loadingMore,
    solicitudId,
    pageSize,
    statusFilter,
    qualificationFilter,
    sortField,
    sortOrder,
    loadCandidates,
    hasRole,
  ])

  /**
   * Show toast notification
   */
  const showToast = (message: string, type: 'positive' | 'negative') => {
    setToastMessage(message)
    setToastType(type)
  }

  /**
   * Close toast notification
   */
  const closeToast = () => {
    setToastMessage(null)
  }

  /**
   * Handle tab change
   */
  const handleTabChange = (tabId: number) => {
    setActiveTab(tabId)
  }

  /**
   * Check if we need to start polling when candidates change
   */
  useEffect(() => {
    if (!candidates || !candidates.length || processingPolling) return

    const hasProcessingCandidates = candidates.some(
      c =>
        c.processing_status === 'uploading' ||
        c.processing_status === 'processing'
    )

    if (hasProcessingCandidates) {
      setProcessingPolling(true)
    }
  }, [candidates, processingPolling])

  /**
   * Real-time polling for processing status updates
   */
  useEffect(() => {
    if (!processingPolling || !solicitudId) return
    const interval = setInterval(async () => {
      try {
        const params = {
          id_solicitud: solicitudId,
          size: pageSize,
          sort_field: sortField,
          sort_order: sortOrder,
        }

        const response: CandidatesResponse =
          await apiService.getCandidates(params)

        const { data } = response
        const items = data.items || []

        setCandidates(items)
        setTotalItems(data.total_items || 0)
        setNextKey(data.next_key)

        // Check if we still have processing candidates
        const stillProcessing = items.some(
          c =>
            c.processing_status === 'uploading' ||
            c.processing_status === 'processing'
        )

        if (!stillProcessing) {
          setProcessingPolling(false)
        }
      } catch (err) {
        console.warn('Polling error:', err)
      }
    }, 5000) // Poll every 5 seconds

    return () => {
      clearInterval(interval)
    }
  }, [processingPolling, solicitudId, pageSize, sortField, sortOrder])

  /**
   * Initial data loading
   */
  useEffect(() => {
    if (solicitudId) {
      loadSolicitudDetails()
      loadCandidates(true)
    } else {
      navigate('/dashboard')
    }
  }, [solicitudId, navigate, loadSolicitudDetails, loadCandidates])

  if (!solicitudId) {
    return null
  }

  // Ensure candidates is always an array
  const safeCandidates = candidates || []

  // Get position name with job definition priority
  const getPositionName = () => {
    if (solicitudData?.job_definition_name) {
      return solicitudData.job_definition_name
    }
    if (solicitudData?.requerimiento?.puesto_solicitado) {
      return solicitudData.requerimiento.puesto_solicitado
    }
    return (
      solicitudData?.jd?.estructura?.puesto_solicitado?.puesto || 'Posición'
    )
  }

  const positionName = getPositionName()

  // Get job definition info for display
  const getJobDefinitionInfo = () => {
    if (
      solicitudData?.job_definition_name &&
      solicitudData?.job_definition_puesto
    ) {
      return `${solicitudData.job_definition_name} - ${solicitudData.job_definition_puesto}`
    }
    if (solicitudData?.job_definition_name) {
      return solicitudData.job_definition_name
    }
    return null
  }

  const jobDefinitionInfo = getJobDefinitionInfo()

  return (
    <AuthGuard>
      <Layout>
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <Flex justify="space-between" align="center">
              <div>
                <Heading type="h1" className="mb-2">
                  {positionName}
                </Heading>
                {jobDefinitionInfo && (
                  <Text className="text-blue-600 font-medium mb-1">
                    Definición de puesto: {jobDefinitionInfo}
                  </Text>
                )}
                <Text type="text3" color="secondary" className="mt-1">
                  {totalItems} candidatos
                </Text>
              </div>
            </Flex>
          </div>

          {/* Tabbed Content */}
          <div className="bg-white rounded-lg shadow-sm">
            <Box className="p-6">
              <TabList
                activeTabId={activeTab}
                onTabChange={handleTabChange}
                tabType="Compact"
                stretchedUnderline
                className="mb-6"
              >
                {tabs.map(tab => (
                  <Tab key={tab.value} value={tab.value}>
                    {tab.label}
                  </Tab>
                ))}
              </TabList>

              <TabPanels activeTabId={activeTab}>
                <TabPanel index={0}>
                  <>
                    {(showUpload || safeCandidates.length === 0) &&
                      !loading && (
                        <Box className="mb-6">
                          <CVUpload
                            solicitudId={solicitudId}
                            onUploadComplete={handleUploadSuccess}
                            onError={error =>
                              showToast(getErrorMessage(error), 'negative')
                            }
                            checkApprovalStatus={true}
                            refreshInterval={30000}
                          />
                        </Box>
                      )}

                    {safeCandidates.length > 0 && !showUpload && (
                      <Flex justify="end" className="mb-4">
                        <Button
                          onClick={() => setShowUpload(true)}
                          kind="primary"
                        >
                          Subir más CVs
                        </Button>
                      </Flex>
                    )}

                    <div className="relative">
                      {!loading && safeCandidates.length > 0 && (
                        <CandidatesTable
                          candidates={safeCandidates}
                          loading={loadingMore}
                          selectedCandidates={selectedCandidates}
                          onCandidateSelection={handleCandidateSelection}
                          onSelectAll={handleSelectAll}
                          onBulkStatusUpdate={handleBulkStatusUpdate}
                          onBulkQualificationUpdate={
                            handleBulkQualificationUpdate
                          }
                          onFilterChange={handleFilterChange}
                          onSortChange={handleSortChange}
                          onLoadMore={nextKey ? handleLoadMore : undefined}
                          bulkUpdateLoading={bulkUpdateLoading}
                          statusFilter={statusFilter}
                          qualificationFilter={qualificationFilter}
                          sortField={sortField}
                          sortOrder={sortOrder}
                          totalItems={totalItems}
                          hasMore={!!nextKey}
                          solicitudId={solicitudId}
                          filterLoading={filterLoading}
                        />
                      )}

                      {/* Filter/Sort Loading Overlay */}
                      {filterLoading && (
                        <div
                          className="inset-0 flex items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 9999,
                            backdropFilter: 'blur(2px)',
                          }}
                        >
                          <div
                            className="bg-white rounded-lg p-8 flex items-center gap-4 border-2 border-gray-200"
                            style={{
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                              minWidth: '250px',
                            }}
                          >
                            <Loader size="medium" />
                            <Text weight="bold">Aplicando filtros...</Text>
                          </div>
                        </div>
                      )}
                    </div>

                    {loading && safeCandidates.length === 0 && (
                      <div className="p-8 text-center">
                        <Loader size="large" />
                        <Text className="mt-4">Cargando candidatos...</Text>
                      </div>
                    )}

                    {error && !loading && (
                      <div className="p-8 text-center">
                        <Text color="negative" className="mb-4">
                          {typeof error === 'string'
                            ? error
                            : 'Ha ocurrido un error inesperado'}
                        </Text>
                        <Button onClick={() => loadCandidates(true)}>
                          Reintentar
                        </Button>
                      </div>
                    )}

                    {!loading &&
                      !error &&
                      safeCandidates.length === 0 &&
                      !showUpload && (
                        <div className="p-8 text-center">
                          <Text className="mb-4">
                            No hay candidatos para esta posición.
                          </Text>
                          <Button onClick={() => setShowUpload(true)}>
                            Subir CVs
                          </Button>
                        </div>
                      )}
                  </>
                </TabPanel>

                <TabPanel index={1}>
                  <JobInformation solicitudId={solicitudId} />
                </TabPanel>

                <TabPanel index={2}>
                  {permissions?.canManageUsers() ? (
                    <JobIntegrationSection
                      solicitudId={solicitudId}
                      onIntegrationChange={() => {}}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <Text color="secondary">
                        No tienes permisos para ver esta sección
                      </Text>
                    </div>
                  )}
                </TabPanel>
              </TabPanels>
            </Box>
          </div>
        </div>

        {/* Toast Notification */}
        {toastMessage && (
          <Toast
            open={!!toastMessage}
            type={toastType}
            onClose={closeToast}
            autoHideDuration={5000}
            className="fixed top-4 right-4 z-50"
          >
            {toastMessage}
          </Toast>
        )}
      </Layout>
    </AuthGuard>
  )
}

export default CandidatesManagement
